import { useEffect, useRef, useState } from "react";
import {
  X,
  Home,
  Briefcase,
  Info,
  Mail,
  Newspaper,
  FolderKanban,
  Users,
  Search,
  Sun,
  Moon,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { mobileDrawerCopy, headerSearchCopy } from "@/lib/siteCopy";
import {
  SITE_LANGUAGES,
  setSiteLanguage,
  type SiteLanguageCode,
} from "@/lib/language";
import { localizeServiceCategories } from "@/lib/localizeServices";

type NavItem = {
  label: string;
  path?: string;
  href?: string;
  id?: string;
  type: "route" | "scroll" | "external";
};

type MobileDrawerProps = {
  open: boolean;
  onClose: () => void;
  navItems: NavItem[];
};

type SearchHit = {
  label: string;
  description: string;
  path?: string;
  href?: string;
};

function iconForNav(item: NavItem) {
  const key = item.id ?? item.path ?? "";
  switch (key) {
    case "/":
      return Home;
    case "services":
    case "/services":
      return Briefcase;
    case "blog":
      return Newspaper;
    case "/work":
      return FolderKanban;
    case "/about":
      return Info;
    case "/careers":
      return Users;
    default:
      return Mail;
  }
}

export function MobileDrawer({ open, onClose, navItems }: MobileDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const lang = useSiteLanguage();
  const t = mobileDrawerCopy[lang];
  const [isDark, setIsDark] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setIsDark(document.documentElement.classList.contains("dark"));
    } else {
      document.body.style.overflow = "";
      setSearchOpen(false);
      setSearchQuery("");
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (open && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (searchOpen) {
      window.setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [searchOpen]);

  const toggleTheme = () => {
    try {
      const next = !document.documentElement.classList.contains("dark");
      if (next) {
        document.documentElement.classList.add("dark");
        localStorage.setItem("theme", "dark");
      } else {
        document.documentElement.classList.remove("dark");
        localStorage.setItem("theme", "light");
      }
      setIsDark(next);
    } catch {
      /* ignore */
    }
  };

  const serviceHits: SearchHit[] = Object.values(
    localizeServiceCategories(lang),
  ).flatMap((category) =>
    category.services.map((service) => ({
      label: service.title,
      description: `${category.title} · ${lang === "hi" ? "सेवा" : "Service"}`,
      path: `/services/${category.id}#${service.id}`,
    })),
  );

  const searchResults = (() => {
    const q = searchQuery.trim().toLowerCase();
    const pool = serviceHits;
    if (!q) return pool.slice(0, 8);
    const rank = (item: SearchHit) => {
      const label = item.label.toLowerCase();
      if (label === q) return 0;
      if (label.startsWith(q)) return 1;
      if (label.includes(q)) return 2;
      return 3;
    };
    return pool
      .filter(
        (item) =>
          item.label.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q),
      )
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, 8);
  })();

  const goToSearchHit = (hit: SearchHit) => {
    setSearchQuery("");
    setSearchOpen(false);
    onClose();
    if (hit.href) {
      window.location.href = hit.href;
      return;
    }
    if (!hit.path) return;
    const [pathname, hash] = hit.path.split("#");
    navigate({
      pathname: pathname || "/",
      hash: hash ? `#${hash}` : undefined,
    });
  };

  const handleNavigation = (item: NavItem) => {
    if (item.type === "external" && item.href) {
      window.location.href = item.href;
      onClose();
    } else if (item.type === "route" && item.path) {
      navigate(item.path);
      onClose();
    } else if (item.type === "scroll" && item.id) {
      if (location.pathname !== "/") {
        navigate("/", { state: { scrollTo: item.id } });
      } else if (item.id) {
        document
          .getElementById(item.id)
          ?.scrollIntoView({ behavior: "smooth" });
      }
      onClose();
    }
  };

  if (!open) return null;

  const drawerNavItems = navItems.filter(
    (it) => it.id !== "contact" && it.id !== "products" && it.label !== "Products",
  );

  return (
    <>
      <button
        aria-label="Close menu"
        tabIndex={open ? 0 : -1}
        onClick={onClose}
        className={`fixed inset-0 z-[1000] lg:hidden overlay-smooth ${
          open
            ? "opacity-100 pointer-events-auto bg-black/60"
            : "opacity-0 pointer-events-none"
        }`}
        style={{ WebkitTapHighlightColor: "transparent" }}
      />
      <nav
        ref={drawerRef}
        tabIndex={-1}
        aria-label="Mobile navigation"
        className={`fixed top-0 left-0 right-0 h-screen w-full bg-white dark:bg-black z-[1100] shadow-xl lg:hidden outline-none transform drawer-smooth ${
          open ? "translate-y-0" : "-translate-y-full pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <BrandLogo size="lg" />
          <div className="flex items-center gap-0.5">
            <div className="mr-1 flex items-center rounded-full border border-neutral-200 p-0.5 dark:border-neutral-800">
              {SITE_LANGUAGES.map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  onClick={() => setSiteLanguage(opt.code as SiteLanguageCode)}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                    lang === opt.code
                      ? "bg-neutral-900 text-white dark:bg-white dark:text-black"
                      : "text-neutral-500 hover:text-neutral-900 dark:hover:text-white"
                  }`}
                  aria-pressed={lang === opt.code}
                  aria-label={opt.label}
                >
                  {opt.short}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="Search"
              aria-expanded={searchOpen}
              className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-900 focus:outline-none transition-colors"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-900 focus:outline-none transition-colors"
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <button
              onClick={onClose}
              aria-label="Close menu"
              className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-900 focus:outline-none transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
            <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2.5">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={headerSearchCopy[lang].placeholder}
                className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                autoComplete="off"
              />
            </div>
            {searchResults.length > 0 && (
              <ul className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-border bg-background py-1">
                {searchResults.map((hit) => (
                  <li key={`d-${hit.label}-${hit.path}`}>
                    <button
                      type="button"
                      onClick={() => goToSearchHit(hit)}
                      className="flex w-full flex-col items-start gap-0.5 px-3.5 py-2.5 text-left hover:bg-muted"
                    >
                      <span className="text-sm font-medium text-foreground">
                        {hit.label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {hit.description}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="px-6 py-8 h-[calc(100vh-72px)] overflow-y-auto">
          <ul className="flex flex-col items-start justify-start gap-4 max-w-2xl mx-auto min-h-[60vh] pl-4">
            {drawerNavItems.map((item: NavItem) => {
              const Icon = iconForNav(item);
              return (
                <li key={item.path ?? item.href ?? item.label} className="w-full">
                  <div className="w-full">
                    <button
                      onClick={() => handleNavigation(item)}
                      className="w-full flex items-center gap-4 px-4 py-4 text-lg font-medium tracking-tight text-neutral-900 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded-lg transition-colors"
                    >
                      <Icon className="w-6 h-6 text-neutral-600 dark:text-neutral-300" />
                      <span className="text-left">{item.label}</span>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          {navItems.find((it) => it.id === "contact") && (
            <div className="w-full mt-8 flex justify-center">
              <button
                onClick={() => {
                  navigate("/contact");
                  onClose();
                }}
                className="silver-border mx-auto max-w-[240px] w-full flex items-center justify-center gap-3 px-6 py-3 text-base font-semibold rounded-full focus:outline-none hover:shadow-lg"
              >
                <Mail className="w-5 h-5 text-white" />
                <span>{t.contact}</span>
              </button>
            </div>
          )}

          <div className="mt-4 max-w-2xl mx-auto w-full text-center">
            <p className="text-sm text-muted-foreground">{t.preferChat}</p>
          </div>
        </div>
      </nav>
    </>
  );
}
